/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors in child components and displays fallback UI.
 * Provides graceful error handling with retry capability.
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    showDetails?: boolean;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary class component for catching React errors.
 */
export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({ errorInfo });

        // Report to external service if callback provided
        this.props.onError?.(error, errorInfo);
    }

    private handleRetry = (): void => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    public render(): ReactNode {
        if (this.state.hasError) {
            // Custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <div
                    className="min-h-[200px] flex items-center justify-center p-8"
                    role="alert"
                    aria-live="assertive"
                >
                    <div className="bg-[#0a2540] border border-red-500/30 rounded-xl p-8 max-w-lg w-full text-center">
                        {/* Error Icon */}
                        <svg
                            className="w-16 h-16 text-red-500 mx-auto mb-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>

                        <h2 className="text-xl font-semibold text-white mb-2">
                            Something went wrong
                        </h2>

                        <p className="text-[var(--text-secondary)] mb-6">
                            An unexpected error occurred. Please try again or contact support if the problem persists.
                        </p>

                        {/* Error details (development or when showDetails is true) */}
                        {(this.props.showDetails || process.env.NODE_ENV === 'development') && this.state.error && (
                            <details className="text-left mb-6 p-4 bg-[var(--bg)] rounded-lg overflow-auto max-h-48">
                                <summary className="text-red-400 cursor-pointer mb-2 focus:outline-none focus:ring-2 focus:ring-red-400 rounded">
                                    Error Details
                                </summary>
                                <pre className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}

                        <button
                            onClick={this.handleRetry}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#F26430] hover:bg-[#e55520] 
                         text-white rounded-lg font-medium transition-colors
                         focus:outline-none focus:ring-2 focus:ring-[#F26430] focus:ring-offset-2 focus:ring-offset-[#0a2540]"
                            aria-label="Retry loading the component"
                        >
                            {/* Retry Icon */}
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                            Retry
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Compact error fallback for smaller components.
 */
export const CompactErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
    <div
        className="flex items-center justify-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg"
        role="alert"
    >
        <svg
            className="w-5 h-5 text-red-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
        </svg>
        <span className="text-red-400 text-sm">Failed to load</span>
        {onRetry && (
            <button
                onClick={onRetry}
                className="text-red-400 hover:text-red-300 underline text-sm focus:outline-none focus:ring-2 focus:ring-red-400 rounded"
            >
                Retry
            </button>
        )}
    </div>
);

/**
 * Higher-order component wrapper for error boundary.
 */
export function withErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    fallback?: ReactNode,
    onError?: (error: Error, errorInfo: ErrorInfo) => void
) {
    return function WithErrorBoundary(props: P) {
        return (
            <ErrorBoundary fallback={fallback} onError={onError}>
                <WrappedComponent {...props} />
            </ErrorBoundary>
        );
    };
}

/**
 * Hook-friendly error boundary wrapper component.
 */
interface ErrorBoundaryWrapperProps {
    children: ReactNode;
    fallback?: ReactNode;
    name?: string;
}

export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
    children,
    fallback,
    name = 'Component'
}) => {
    const handleError = (error: Error, errorInfo: ErrorInfo) => {
        // Log to console with component name
        console.error(`[${name}] Error:`, error);
        console.error(`[${name}] Component Stack:`, errorInfo.componentStack);

        // Could send to error tracking service here
        // e.g., Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    };

    return (
        <ErrorBoundary fallback={fallback} onError={handleError}>
            {children}
        </ErrorBoundary>
    );
};

export default ErrorBoundary;
